import React from 'react'

import type {
  ChartSegmentMeta,
  ChartSegmentView,
  ChartSegmentViewRequest,
} from '@digital-twins/shared-types'

export interface ChartSegmentContextValue {
  studyId: string
  meta: ChartSegmentMeta | null
  refreshMeta: () => Promise<void>
  getView: (
    slideId: string,
    request: ChartSegmentViewRequest,
    signal?: AbortSignal,
  ) => Promise<ChartSegmentView>
  /** The segment UI's chrome, in the viewer's language. The host supplies every string. */
  labels: ChartSegmentLabels
}

/**
 * The gear, menu, tabs and breakdown states are the viewer's interface, not
 * slide content, so their words come from the host app's own i18n. deck-ui
 * ships no copy of its own for them.
 */
export interface ChartSegmentLabels {
  /** The Off option on a chart drawn whole. */
  offWholeSample: string
  /**
   * The Off option on a chart the deck already draws split by a cut, where Off
   * returns to that split rather than to the whole sample.
   */
  offShowsSplit: string
  /** Accessible name of the gear button and its menu. */
  breakDownPerSegment: string
  /** The menu's heading. */
  breakDownBy: string
  /** The word the gear pill unfurls on hover. */
  breakDown: string
  /** Some axes loaded and some did not. */
  axesPartlyUnavailable: string
  /** No axis loaded. */
  axesUnavailable: string
  retry: string
  /** Menu section of demographic axes. */
  demographics: string
  /** Menu section of the user's own segments. */
  yourSegments: string
  /** How many rows a demographic axis produces. */
  groupCount: (count: number) => string
  loading: string
  /** A breakdown request failed. */
  viewFailed: string
  /** A breakdown came back with no data; `axisLabel` names the axis. */
  noBreakdown: (axisLabel: string) => string
  /** The axis name `noBreakdown` uses when the selected axis is no longer listed. */
  thisSegment: string
  /** Accessible name of the segment tab strip. */
  tabs: string
  showMoreSegments: string
  showPreviousSegments: string
}

const ChartSegmentContext = React.createContext<ChartSegmentContextValue | null>(null)

export function ChartSegmentProvider(props: {
  value: ChartSegmentContextValue
  children: React.ReactNode
}) {
  const { value, children } = props
  return (
    <ChartSegmentContext.Provider value={value}>
      {children}
    </ChartSegmentContext.Provider>
  )
}

export function useChartSegmentContext(): ChartSegmentContextValue | null {
  return React.useContext(ChartSegmentContext)
}
